import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  Plus,
  Calendar,
  MapPin,
  Users,
  CheckCircle,
  Clock,
  MessageSquare,
  Send,
  Eye,
  Edit,
  Trash2,
  BarChart3,
  Paperclip,
  FileText,
  Download,
  X,
  Upload,
  Image,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const EventsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [departmentFields, setDepartmentFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [availableHotels, setAvailableHotels] = useState([]);

  const [filter, setFilter] = useState("all");

  // Create/Edit event modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: "",
    client_name: "",
    event_type: "",
    date_start: "",
    date_end: "",
    location: "",
    description: "",
    status: "draft",
    hotels: [],
    sections: {},
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // View event modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);

  const isEventManager =
    user?.department === "meeting_events" ||
    ["admin", "management", "super_admin"].includes(user?.role);

  useEffect(() => {
    fetchEvents();
    fetchDepartmentFields();
  }, [filter]);

  const fetchEvents = async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const [evRes, hotRes] = await Promise.all([
        axios.get(`${API}/events${params}`),
        axios.get(`${API}/hotels`),
      ]);
      setEvents(evRes.data);
      setAvailableHotels(hotRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento eventi");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentFields = async () => {
    try {
      const response = await axios.get(`${API}/events/department-fields`);
      setDepartmentFields(response.data);
    } catch (error) {
      console.error("Error fetching department fields:", error);
    }
  };

  const openCreateModal = () => {
    setEditingEvent(null);
    setEventForm({
      name: "",
      client_name: "",
      event_type: "",
      date_start: "",
      date_end: "",
      location: "",
      description: "",
      status: "draft",
      hotels: user?.selected_hotel ? [user.selected_hotel] : [],
      sections: {},
    });
    setActiveSection(null);
    setEditModalOpen(true);
  };

  const openEditModal = async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      const event = response.data;
      
      // Convert sections array to object
      const sectionsObj = {};
      event.sections.forEach((s) => {
        sectionsObj[s.department_id] = s.data;
      });
      
      setEditingEvent(event);
      setEventForm({
        name: event.name,
        client_name: event.client_name || "",
        event_type: event.event_type || "",
        date_start: event.date_start?.split("T")[0] || "",
        date_end: event.date_end?.split("T")[0] || "",
        location: event.location || "",
        description: event.description || "",
        status: event.status || "draft",
        hotels: event.hotels || [],
        sections: sectionsObj,
      });
      setActiveSection(null);
      setEditModalOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento evento");
    }
  };

  const handleSaveEvent = async () => {
    if (!eventForm.name || !eventForm.date_start) {
      toast.error("Nome e data inizio sono obbligatori");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        ...eventForm,
        date_start: new Date(eventForm.date_start).toISOString(),
        date_end: eventForm.date_end
          ? new Date(eventForm.date_end).toISOString()
          : null,
      };

      if (editingEvent) {
        await axios.patch(`${API}/events/${editingEvent.id}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success("Evento creato");
      }

      setEditModalOpen(false);
      fetchEvents();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore salvataggio");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo evento?")) return;

    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success("Evento eliminato");
      fetchEvents();
    } catch (error) {
      toast.error("Errore eliminazione");
    }
  };

  const openViewModal = async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setViewingEvent(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento evento");
    }
  };

  const handleAcknowledge = async (eventId, departmentId) => {
    try {
      await axios.post(
        `${API}/events/${eventId}/sections/${departmentId}/acknowledge`
      );
      toast.success("Presa visione confermata");
      
      // Refresh event
      const response = await axios.get(`${API}/events/${eventId}`);
      setViewingEvent(response.data);
      fetchEvents();
    } catch (error) {
      toast.error("Errore nella conferma");
    }
  };

  const handleAddNote = async (eventId, departmentId) => {
    if (!noteText.trim()) return;

    setSendingNote(true);
    try {
      await axios.post(
        `${API}/events/${eventId}/sections/${departmentId}/notes`,
        { note: noteText }
      );
      
      toast.success("Nota aggiunta");
      setNoteText("");
      
      // Refresh event
      const response = await axios.get(`${API}/events/${eventId}`);
      setViewingEvent(response.data);
    } catch (error) {
      toast.error("Errore nell'aggiunta nota");
    } finally {
      setSendingNote(false);
    }
  };

  const handleFileUpload = async (eventId, departmentId, file) => {
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }
    
    setUploadingFile(departmentId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(
        `${API}/events/${eventId}/sections/${departmentId}/attachments`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("File caricato");
      
      const response = await axios.get(`${API}/events/${eventId}`);
      setViewingEvent(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nel caricamento file");
    } finally {
      setUploadingFile(null);
    }
  };

  const handleDeleteAttachment = async (eventId, departmentId, attachmentId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/sections/${departmentId}/attachments/${attachmentId}`
      );
      toast.success("Allegato rimosso");
      
      const response = await axios.get(`${API}/events/${eventId}`);
      setViewingEvent(response.data);
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const handleDownloadFile = async (storagePath, filename) => {
    try {
      const response = await axios.get(`${API}/files/${storagePath}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Errore nel download del file");
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return "text-red-500";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "text-blue-500";
    if (["doc", "docx"].includes(ext)) return "text-blue-700";
    if (["xls", "xlsx", "csv"].includes(ext)) return "text-emerald-600";
    return "text-stone-500";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImageFile = (filename) => {
    const ext = filename?.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const [imagePreviews, setImagePreviews] = useState({});
  const [expandedImage, setExpandedImage] = useState(null);

  const loadImagePreview = async (attachmentId, storagePath) => {
    if (imagePreviews[attachmentId]) return;
    try {
      const response = await axios.get(`${API}/files/${storagePath}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      setImagePreviews((prev) => ({ ...prev, [attachmentId]: url }));
    } catch {
      // silently fail
    }
  };

  const updateSectionField = (deptId, fieldKey, value) => {
    setEventForm((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [deptId]: {
          ...(prev.sections[deptId] || {}),
          [fieldKey]: value,
        },
      },
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-stone-100 text-stone-700",
      published: "bg-blue-100 text-blue-700",
      in_progress: "bg-amber-100 text-amber-700",
      completed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-red-100 text-red-700",
    };
    const labels = {
      draft: "Bozza",
      published: "Pubblicato",
      in_progress: "In Corso",
      completed: "Completato",
      cancelled: "Annullato",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(parseISO(dateStr), "d MMM yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-stone-600 hover:text-stone-900"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-outfit text-xl font-bold text-stone-900">
              Eventi
            </h1>
            <p className="text-xs text-stone-500">Ordini di servizio</p>
          </div>
          <div className="flex items-center gap-2">
            {isEventManager && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/events/dashboard")}
                className="text-stone-600 hover:text-stone-900"
                data-testid="events-dashboard-button"
              >
                <BarChart3 className="w-5 h-5" />
              </Button>
            )}
            {isEventManager && (
              <Button
                onClick={openCreateModal}
                className="rounded-full bg-stone-900 hover:bg-stone-800"
                data-testid="create-event-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo
              </Button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto">
          {[
            { id: "all", label: "Tutti" },
            { id: "published", label: "Attivi" },
            { id: "draft", label: "Bozze" },
            { id: "completed", label: "Completati" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                filter === f.id
                  ? "bg-stone-900 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      {/* Events List */}
      <main className="p-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-4 border border-stone-200 animate-pulse"
              >
                <div className="h-5 bg-stone-200 rounded w-1/2 mb-2" />
                <div className="h-4 bg-stone-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-stone-400">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nessun evento trovato</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="events-list">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-stone-900">{event.name}</h3>
                    {event.client_name && (
                      <p className="text-sm text-stone-500">{event.client_name}</p>
                    )}
                  </div>
                  {getStatusBadge(event.status)}
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-stone-600 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(event.date_start)}</span>
                  </div>
                  {event.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{event.location}</span>
                    </div>
                  )}
                </div>

                {/* Department sections summary */}
                {event.sections && event.sections.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {event.sections.map((section) => (
                      <span
                        key={section.id}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                          section.acknowledged
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {section.acknowledged && <CheckCircle className="w-3 h-3" />}
                        {section.department_name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-stone-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openViewModal(event.id)}
                    className="flex-1"
                    data-testid={`view-event-${event.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Dettagli
                  </Button>
                  {isEventManager && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(event.id)}
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteEvent(event.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Event Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingEvent ? "Modifica Evento" : "Nuovo Evento"}
            </DialogTitle>
            <DialogDescription>
              Compila i dettagli dell'evento e le sezioni per ogni reparto
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nome Evento *</Label>
                <Input
                  value={eventForm.name}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, name: e.target.value })
                  }
                  placeholder="es: Matrimonio Rossi"
                  data-testid="event-name"
                />
              </div>
              <div>
                <Label>Cliente</Label>
                <Input
                  value={eventForm.client_name}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, client_name: e.target.value })
                  }
                  placeholder="Nome cliente"
                />
              </div>
              <div>
                <Label>Tipo Evento</Label>
                <Select
                  value={eventForm.event_type}
                  onValueChange={(value) =>
                    setEventForm({ ...eventForm, event_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wedding">Matrimonio</SelectItem>
                    <SelectItem value="conference">Conferenza</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="gala">Gala/Cena</SelectItem>
                    <SelectItem value="party">Festa</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data Inizio *</Label>
                <Input
                  type="date"
                  value={eventForm.date_start}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, date_start: e.target.value })
                  }
                  data-testid="event-date-start"
                />
              </div>
              <div>
                <Label>Data Fine</Label>
                <Input
                  type="date"
                  value={eventForm.date_end}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, date_end: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={eventForm.location}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, location: e.target.value })
                  }
                  placeholder="es: Sala Congressi"
                />
              </div>
              <div>
                <Label>Stato</Label>
                <Select
                  value={eventForm.status}
                  onValueChange={(value) =>
                    setEventForm({ ...eventForm, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="published">Pubblicato</SelectItem>
                    <SelectItem value="in_progress">In Corso</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={eventForm.description}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, description: e.target.value })
                  }
                  placeholder="Descrizione generale dell'evento..."
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <Label>Hotel</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {availableHotels.map((h) => (
                    <label key={h.slug} className="flex items-center gap-2 cursor-pointer">
                      <input
                        data-testid={`event-hotel-${h.slug}`}
                        type="checkbox"
                        checked={(eventForm.hotels || []).includes(h.slug)}
                        onChange={(e) => {
                          const hotels = e.target.checked
                            ? [...(eventForm.hotels || []), h.slug]
                            : (eventForm.hotels || []).filter((x) => x !== h.slug);
                          setEventForm({ ...eventForm, hotels });
                        }}
                        className="rounded border-stone-300"
                      />
                      <span className="text-sm">{h.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-stone-400 mt-1">Seleziona in quali hotel sara' visibile l'evento</p>
              </div>
            </div>

            {/* Department Sections */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-stone-900 mb-3">
                Sezioni Reparti
              </h3>
              <p className="text-sm text-stone-500 mb-4">
                Clicca su un reparto per aggiungere le informazioni specifiche
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(departmentFields).map(([deptId, dept]) => {
                  const hasData =
                    eventForm.sections[deptId] &&
                    Object.values(eventForm.sections[deptId]).some((v) => v);
                  return (
                    <button
                      key={deptId}
                      onClick={() =>
                        setActiveSection(activeSection === deptId ? null : deptId)
                      }
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeSection === deptId
                          ? "bg-stone-900 text-white"
                          : hasData
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                      }`}
                    >
                      {dept.name}
                      {hasData && activeSection !== deptId && (
                        <CheckCircle className="w-3 h-3 inline ml-1" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active section fields */}
              {activeSection && departmentFields[activeSection] && (
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                  <h4 className="font-semibold text-stone-900 mb-3">
                    {departmentFields[activeSection].name}
                  </h4>
                  <div className="grid gap-4">
                    {departmentFields[activeSection].fields.map((field) => (
                      <div key={field.key}>
                        <Label>{field.label}</Label>
                        {field.type === "textarea" ? (
                          <Textarea
                            value={
                              eventForm.sections[activeSection]?.[field.key] || ""
                            }
                            onChange={(e) =>
                              updateSectionField(
                                activeSection,
                                field.key,
                                e.target.value
                              )
                            }
                            placeholder={field.placeholder}
                            rows={2}
                          />
                        ) : (
                          <Input
                            type={field.type}
                            value={
                              eventForm.sections[activeSection]?.[field.key] || ""
                            }
                            onChange={(e) =>
                              updateSectionField(
                                activeSection,
                                field.key,
                                e.target.value
                              )
                            }
                            placeholder={field.placeholder}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleSaveEvent}
              disabled={saveLoading}
              className="bg-stone-900 hover:bg-stone-800"
              data-testid="save-event-button"
            >
              {saveLoading
                ? "Salvataggio..."
                : editingEvent
                ? "Salva Modifiche"
                : "Crea Evento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Event Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              {viewingEvent?.name}
              {viewingEvent && getStatusBadge(viewingEvent.status)}
            </DialogTitle>
            <DialogDescription>
              {viewingEvent?.client_name && `Cliente: ${viewingEvent.client_name}`}
            </DialogDescription>
          </DialogHeader>

          {viewingEvent && (
            <div className="space-y-4 py-4">
              {/* Event Info */}
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-1 text-stone-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(viewingEvent.date_start)}</span>
                  {viewingEvent.date_end && (
                    <span> - {formatDate(viewingEvent.date_end)}</span>
                  )}
                </div>
                {viewingEvent.location && (
                  <div className="flex items-center gap-1 text-stone-600">
                    <MapPin className="w-4 h-4" />
                    <span>{viewingEvent.location}</span>
                  </div>
                )}
              </div>

              {viewingEvent.description && (
                <p className="text-stone-600 text-sm bg-stone-50 p-3 rounded-lg">
                  {viewingEvent.description}
                </p>
              )}

              {/* Department Sections */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-900">
                  Informazioni per Reparto
                </h3>

                {viewingEvent.sections.map((section) => (
                  <div
                    key={section.id}
                    className="bg-white border border-stone-200 rounded-xl overflow-hidden"
                  >
                    <div
                      className={`px-4 py-3 flex items-center justify-between ${
                        section.acknowledged
                          ? "bg-emerald-50 border-b border-emerald-100"
                          : "bg-amber-50 border-b border-amber-100"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          {section.department_name}
                        </span>
                        {section.acknowledged && (
                          <span className="text-xs text-emerald-700 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Confermato da {section.acknowledged_by_name}
                          </span>
                        )}
                      </div>
                      {!section.acknowledged &&
                        (user?.department === section.department_id ||
                          ["admin", "management", "super_admin"].includes(user?.role)) && (
                          <Button
                            size="sm"
                            onClick={() =>
                              handleAcknowledge(viewingEvent.id, section.department_id)
                            }
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`acknowledge-${section.department_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Conferma Presa Visione
                          </Button>
                        )}
                    </div>

                    <div className="p-4">
                      {/* Section data */}
                      <div className="grid gap-2 text-sm">
                        {Object.entries(section.data || {}).map(([key, value]) => {
                          if (!value) return null;
                          const fieldDef = departmentFields[
                            section.department_id
                          ]?.fields?.find((f) => f.key === key);
                          return (
                            <div key={key}>
                              <span className="font-medium text-stone-700">
                                {fieldDef?.label || key}:
                              </span>{" "}
                              <span className="text-stone-600 whitespace-pre-wrap">
                                {value}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Attachments */}
                      {section.attachments && section.attachments.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1">
                            <Paperclip className="w-4 h-4" />
                            Allegati ({section.attachments.length})
                          </h4>
                          
                          {/* Image previews grid */}
                          {section.attachments.filter((a) => isImageFile(a.original_filename)).length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                              {section.attachments
                                .filter((att) => isImageFile(att.original_filename))
                                .map((att) => {
                                  if (!imagePreviews[att.id]) {
                                    loadImagePreview(att.id, att.storage_path);
                                  }
                                  return (
                                    <div key={att.id} className="relative group rounded-lg overflow-hidden border border-stone-200 bg-stone-100">
                                      {imagePreviews[att.id] ? (
                                        <img
                                          src={imagePreviews[att.id]}
                                          alt={att.original_filename}
                                          className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                          onClick={() => setExpandedImage(imagePreviews[att.id])}
                                          data-testid={`image-preview-${att.id}`}
                                        />
                                      ) : (
                                        <div className="w-full h-32 flex items-center justify-center">
                                          <div className="w-5 h-5 border-2 border-stone-300 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                      )}
                                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <p className="text-xs text-white truncate">{att.original_filename}</p>
                                      </div>
                                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-sm hover:bg-white"
                                          onClick={() => handleDownloadFile(att.storage_path, att.original_filename)}
                                        >
                                          <Download className="w-3 h-3 text-stone-700" />
                                        </button>
                                        {(user?.department === section.department_id ||
                                          user?.department === "meeting_events" ||
                                          ["admin", "management", "super_admin"].includes(user?.role)) && (
                                          <button
                                            className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-sm hover:bg-red-50"
                                            onClick={() => handleDeleteAttachment(viewingEvent.id, section.department_id, att.id)}
                                          >
                                            <X className="w-3 h-3 text-red-500" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                          
                          {/* Non-image files list */}
                          <div className="space-y-2">
                            {section.attachments
                              .filter((att) => !isImageFile(att.original_filename))
                              .map((att) => (
                              <div
                                key={att.id}
                                className="flex items-center gap-2 bg-stone-50 rounded-lg p-2 group"
                              >
                                <FileText className={`w-5 h-5 flex-shrink-0 ${getFileIcon(att.original_filename)}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-stone-700 truncate">
                                    {att.original_filename}
                                  </p>
                                  <p className="text-xs text-stone-400">
                                    {formatFileSize(att.size)} - {att.uploaded_by_name}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-stone-400 hover:text-stone-700"
                                  onClick={() => handleDownloadFile(att.storage_path, att.original_filename)}
                                  data-testid={`download-attachment-${att.id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {(user?.department === section.department_id ||
                                  user?.department === "meeting_events" ||
                                  ["admin", "management", "super_admin"].includes(user?.role)) && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-stone-400 hover:text-red-500"
                                    onClick={() => handleDeleteAttachment(viewingEvent.id, section.department_id, att.id)}
                                    data-testid={`delete-attachment-${att.id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {section.notes && section.notes.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            Note
                          </h4>
                          <div className="space-y-2">
                            {section.notes.map((note) => (
                              <div
                                key={note.id}
                                className="bg-stone-50 rounded-lg p-2 text-sm"
                              >
                                <div className="flex items-center gap-1 text-stone-500 text-xs mb-1">
                                  <span className="font-medium text-stone-700">
                                    {note.user_name}
                                  </span>
                                  <span>•</span>
                                  <span>
                                    {format(
                                      parseISO(note.created_at),
                                      "d MMM HH:mm",
                                      { locale: it }
                                    )}
                                  </span>
                                </div>
                                <p className="text-stone-700">{note.text}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add note and upload */}
                      {(user?.department === section.department_id ||
                        user?.department === "meeting_events" ||
                        ["admin", "management", "super_admin"].includes(user?.role)) && (
                        <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
                          {/* File Upload */}
                          <div>
                            <label
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-stone-300 text-sm text-stone-600 hover:bg-stone-50 hover:border-stone-400 transition-colors cursor-pointer ${
                                uploadingFile === section.department_id ? "opacity-50 pointer-events-none" : ""
                              }`}
                              data-testid={`upload-attachment-${section.department_id}`}
                            >
                              {uploadingFile === section.department_id ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-stone-400 border-t-transparent rounded-full animate-spin" />
                                  Caricamento...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4" />
                                  Allega file
                                </>
                              )}
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleFileUpload(viewingEvent.id, section.department_id, e.target.files[0]);
                                    e.target.value = "";
                                  }
                                }}
                                disabled={uploadingFile === section.department_id}
                              />
                            </label>
                            <span className="text-xs text-stone-400 ml-2">PDF, immagini, documenti (max 10MB)</span>
                          </div>
                          
                          {/* Note input */}
                          <div className="flex gap-2">
                            <Input
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Aggiungi una nota..."
                              className="flex-1"
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  handleAddNote(viewingEvent.id, section.department_id);
                                }
                              }}
                            />
                            <Button
                              size="icon"
                              onClick={() =>
                                handleAddNote(viewingEvent.id, section.department_id)
                              }
                              disabled={sendingNote || !noteText.trim()}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewModalOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
          data-testid="image-lightbox"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/40 transition-colors"
            onClick={() => setExpandedImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={expandedImage}
            alt="Anteprima"
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default EventsPage;
